import React from 'react';
import PasswordInput from './PasswordInput';
import SmsConsentCheckbox from './SmsConsentCheckbox';
import { LOGO } from '../constants/images';
import { IS_DEVELOPMENT, IS_LOCAL } from '../config';
import { getPasswordStrengthErrors } from '../utils/passwordValidation';

/**
 * AuthenticationModal - Login/Register form with verification, forgot password, and reset password flows.
 * Extracted from App.js to reduce component size (~350 lines of JSX).
 */
function AuthenticationModal({
  // Login/Register state
  loginMode,
  credentials,
  smsConsent,
  onAuth,
  onCredentialsChange,
  onSmsConsentChange,
  onLoginModeChange,
  onClose,
  // Verification state
  showVerificationModal,
  verificationData,
  resendCooldown,
  resending,
  onResendVerification,
  onVerificationClose,
  // Forgot Password state
  showForgotPassword,
  forgotEmail,
  resetMessage,
  onForgotPassword,
  onForgotEmailChange,
  onForgotPasswordClose,
  // Reset Password state
  showResetPassword,
  newPassword,
  confirmNewPassword,
  resetError,
  resetSuccess,
  onResetPassword,
  onNewPasswordChange,
  onConfirmNewPasswordChange
}) {
  return (
    <>
      {/* Login/Register Modal - truly modal, no click-outside-to-close */}
      <div className="modal-overlay">
        <div className="modal-content password-modal">
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--color-text)',
              padding: '0.5rem',
              lineHeight: 1
            }}
            aria-label="Close"
          >
            ×
          </button>

          <div className="auth-form-content">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <img
                src={LOGO}
                alt="Brings to Mind Logo"
                style={{ height: '60px', width: 'auto' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <h1 style={{ margin: 0, textAlign: 'left', fontSize: '1.5rem' }}>
                  BRINGS TO MIND{IS_DEVELOPMENT ? ' - Development' : IS_LOCAL ? ' - Local Dev' : ''}
                </h1>
                <p style={{ margin: 0, textAlign: 'left', fontSize: '0.85rem' }}>REFLECTIONS ON ALL LIFE'S MOMENTS</p>
              </div>
            </div>

            <form onSubmit={onAuth}>
              <input
                type="text"
                placeholder={loginMode === 'login' ? "Username or Email" : "Username"}
                value={credentials.username}
                onChange={(e) => onCredentialsChange({ ...credentials, username: e.target.value })}
                required
              />

              {loginMode === 'register' && (
                <>
                  <input
                    type="email"
                    placeholder="Email"
                    value={credentials.email}
                    onChange={(e) => onCredentialsChange({ ...credentials, email: e.target.value })}
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number (optional)"
                    value={credentials.phone}
                    onChange={(e) => onCredentialsChange({ ...credentials, phone: e.target.value })}
                  />
                  <PasswordInput
                    placeholder="Password"
                    value={credentials.password}
                    onChange={(e) => onCredentialsChange({ ...credentials, password: e.target.value })}
                    required
                  />
                  {credentials.password && (
                    <div className="password-strength" style={{ margin: '0.25rem 0 0.5rem', fontSize: '0.9rem' }}>
                      {(() => {
                        const errors = getPasswordStrengthErrors(credentials.password);
                        const rules = [
                          { label: 'At least 8 characters', met: !errors.includes('At least 8 characters') },
                          { label: 'One uppercase letter', met: !errors.includes('One uppercase letter') },
                          { label: 'One lowercase letter', met: !errors.includes('One lowercase letter') },
                          { label: 'One number', met: !errors.includes('One number') },
                          { label: 'One special character', met: !errors.includes('One special character') },
                        ];
                        return rules.map((rule, i) => (
                          <div key={i} style={{ color: rule.met ? 'green' : '#cc0000', lineHeight: '1.4' }}>
                            {rule.met ? '\u2713' : '\u2717'} {rule.label}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  <PasswordInput
                    placeholder="Confirm Password"
                    name="passwordConfirm"
                    value={credentials.passwordConfirm}
                    onChange={(e) => onCredentialsChange({ ...credentials, passwordConfirm: e.target.value })}
                    required
                  />
                  {credentials.phone && (
                    <SmsConsentCheckbox
                      checked={smsConsent}
                      onChange={onSmsConsentChange}
                      context="registration"
                    />
                  )}
                  {/* Honeypot field - hidden from real users */}
                  <input
                    type="text"
                    name="website"
                    value={credentials.website || ''}
                    onChange={(e) => onCredentialsChange({ ...credentials, website: e.target.value })}
                    style={{ position: 'absolute', left: '-9999px' }}
                    tabIndex={-1}
                    aria-hidden="true"
                    autoComplete="off"
                  />
                  {/* Math question for bot prevention */}
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ fontSize: '1.1rem', fontWeight: 'bold', display: 'block', marginBottom: '0.25rem' }}>
                      Security Question: What is {credentials.mathA} + {credentials.mathB}?
                    </label>
                    <input
                      type="number"
                      placeholder="Enter your answer"
                      value={credentials.mathAnswer}
                      onChange={(e) => onCredentialsChange({ ...credentials, mathAnswer: e.target.value })}
                      required
                      style={{ fontSize: '1.1rem' }}
                    />
                  </div>
                </>
              )}

              {loginMode === 'login' && (
                <PasswordInput
                  placeholder="Password"
                  value={credentials.password}
                  onChange={(e) => onCredentialsChange({ ...credentials, password: e.target.value })}
                  required
                />
              )}

              <button type="submit">{loginMode === 'login' ? 'Login' : 'Register'}</button>
            </form>

            <button
              className="toggle-mode"
              onClick={() => {
                onLoginModeChange(loginMode === 'login' ? 'register' : 'login');
                onCredentialsChange({ username: '', email: '', phone: '', password: '', passwordConfirm: '', website: '', mathA: Math.floor(Math.random() * 10) + 1, mathB: Math.floor(Math.random() * 10) + 1, mathAnswer: '' });
                onSmsConsentChange(false);
              }}
            >
              {loginMode === 'login' ? 'Need an account? Register' : 'Have an account? Login'}
            </button>

            {loginMode === 'login' && (
              <>
                <button
                  className="forgot-password-link"
                  onClick={() => onForgotPasswordClose(true)}
                >
                  Forgot Password?
                </button>
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary, #888)', marginTop: '0.5rem', textAlign: 'center' }}>
                  Haven't verified your account yet? Just log in and we'll resend the verification.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="modal-overlay">
          <div className="modal-content password-modal">
            <h2>Verify Your Account</h2>

            {verificationData.message && (
              <p style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--color-background-secondary)', borderRadius: '0.5rem', color: 'var(--color-text)' }}>
                {verificationData.message}
              </p>
            )}

            {verificationData.hasEmail && (
              <div className="profile-form" style={{ marginBottom: '1.5rem' }}>
                <p style={{ marginBottom: '0.75rem' }}>
                  A verification link was sent to <strong>{verificationData.email}</strong>. Click it, then come back and log in.
                </p>
                {verificationData.hasPhone && (
                  <p style={{ marginBottom: '0.75rem', color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>
                    Once your email is verified, we'll text a code to {verificationData.phone} to confirm your phone number too.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => onResendVerification('email')}
                  disabled={resendCooldown > 0 || resending}
                  className="btn-utility"
                >
                  {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Email'}
                </button>
              </div>
            )}

            <div className="modal-actions">
              <button type="button" onClick={onVerificationClose} className="btn-cancel">
                I'll finish this later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay">
          <div className="modal-content password-modal">
            <h2>Forgot Password</h2>
            <p>Enter your email address and we'll send you a link to reset your password.</p>

            <form onSubmit={onForgotPassword} className="profile-form">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => onForgotEmailChange(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </div>

              {resetMessage && (
                <div className="reset-message">{resetMessage}</div>
              )}

              <div className="modal-actions">
                <button type="submit">Send Reset Link</button>
                <button type="button" onClick={() => onForgotPasswordClose(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="modal-overlay">
          <div className="modal-content password-modal">
            <h2>Reset Password</h2>

            {resetSuccess ? (
              <div className="success-message">
                <p>Password reset successfully!</p>
                <p>Redirecting to login...</p>
              </div>
            ) : (
              <form onSubmit={onResetPassword} className="profile-form">
                <div className="form-group">
                  <label>New Password</label>
                  <PasswordInput
                    value={newPassword}
                    onChange={(e) => onNewPasswordChange(e.target.value)}
                    required
                    placeholder="Enter new password"
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <PasswordInput
                    name="confirmNewPassword"
                    value={confirmNewPassword}
                    onChange={(e) => onConfirmNewPasswordChange(e.target.value)}
                    required
                    placeholder="Confirm new password"
                  />
                </div>

                {resetError && (
                  <div className="error-message">{resetError}</div>
                )}

                <div className="modal-actions">
                  <button type="submit">Reset Password</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default AuthenticationModal;
